import LangEnum from '../../../lang/LangEnum';
import { LangEnumOption } from '../../../lib/OptionsLib';
import { AvailabilityEnum, CompactAvailability } from '../ProductTypes';

export type PreOrderAvailability =
  | AvailabilityEnum.INFINITE_PRE_ORDER
  | AvailabilityEnum.MATERIAL_PRE_ORDER
  | AvailabilityEnum.FUTURE_STOCK;

export const preOrderAvailabilities: PreOrderAvailability[] = [
  AvailabilityEnum.INFINITE_PRE_ORDER,
  AvailabilityEnum.MATERIAL_PRE_ORDER,
  AvailabilityEnum.FUTURE_STOCK,
];

export const isPreOrder = (
  availability: AvailabilityEnum,
): availability is PreOrderAvailability => {
  return preOrderAvailabilities.includes(availability as PreOrderAvailability);
};

export const isPreOrderOrMaterial = (availability: AvailabilityEnum) => {
  return [AvailabilityEnum.INFINITE_PRE_ORDER, AvailabilityEnum.MATERIAL_PRE_ORDER].includes(
    availability,
  );
};

export type OfflineAvailability =
  | AvailabilityEnum.READY_FOR_DELIVERY
  | AvailabilityEnum.INFINITE_PRE_ORDER
  | AvailabilityEnum.EMPTY
  | AvailabilityEnum.FUTURE_STOCK;

export const toOfflineAvailability = (availability: AvailabilityEnum): OfflineAvailability =>
  isPreOrderOrMaterial(availability)
    ? AvailabilityEnum.INFINITE_PRE_ORDER
    : (availability as OfflineAvailability);

export const transformAvailabilityToPreOrder = (availability: AvailabilityEnum) =>
  isPreOrder(availability) ? AvailabilityEnum.INFINITE_PRE_ORDER : availability;

export const availabilityToCompactAvailability = (
  availability: AvailabilityEnum,
): CompactAvailability | undefined => {
  if (isPreOrder(availability)) {
    return CompactAvailability.PRE_ORDER;
  }
  if (availability === AvailabilityEnum.READY_FOR_DELIVERY) {
    return CompactAvailability.READY_FOR_DELIVERY;
  }
  return undefined;
};

export const availabilityToAvailabilityEnum = (availability: string): AvailabilityEnum => {
  if (availability === 'READY_FOR_DELIVERY') {
    return AvailabilityEnum.READY_FOR_DELIVERY;
  }
  if (availability === 'INFINITE_PRE_ORDER') {
    return AvailabilityEnum.INFINITE_PRE_ORDER;
  }
  if (availability === 'MATERIAL_PRE_ORDER') {
    return AvailabilityEnum.MATERIAL_PRE_ORDER;
  }
  if (availability === 'EMPTY') {
    return AvailabilityEnum.EMPTY;
  }
  if (availability === 'FUTURE_STOCK') {
    return AvailabilityEnum.FUTURE_STOCK;
  }
  throw new Error('Invalid availability');
};

export const skuAvailabilityOptions: Array<LangEnumOption & { value: AvailabilityEnum }> = [
  { value: AvailabilityEnum.READY_FOR_DELIVERY, label: LangEnum.GLOBAL_READY_FOR_DELIVERY },
  { value: AvailabilityEnum.FUTURE_STOCK, label: LangEnum.GLOBAL_FUTURE_STOCK },
  { value: AvailabilityEnum.INFINITE_PRE_ORDER, label: LangEnum.GLOBAL_PRE_ORDER },
  { value: AvailabilityEnum.MATERIAL_PRE_ORDER, label: LangEnum.GLOBAL_MATERIAL_PRE_ORDER },
];

export const expandLegacyPreOrderFilter = (value: unknown): string[] | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }

  const values = (Array.isArray(value) ? value : [value]) as string[];

  return [
    ...new Set(
      values.flatMap((item) =>
        item === CompactAvailability.PRE_ORDER ? preOrderAvailabilities : [item],
      ),
    ),
  ];
};
